import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  /** Rich tooltip body — rendered inside the card chrome. */
  content: ReactNode;
  /** The trigger element(s). */
  children: ReactNode;
  /** Card width in px. Default 230. */
  width?: number;
  /** Wrapper class for the trigger (defaults to an inline-flex, cursor-default span). */
  className?: string;
}

const MARGIN = 8; // min gap from any viewport edge
const GAP = 6;    // gap between trigger and card

/**
 * Hover/focus tooltip rendered through a portal on `document.body`, so it
 * escapes any `overflow:hidden` scroll container or Card that would otherwise
 * clip it. Position is measured from the trigger each time it opens (and on
 * scroll/resize): it prefers placing the card ABOVE the trigger, flips BELOW
 * when there isn't room, and clamps the left edge inside the viewport so the
 * card never truncates regardless of where the trigger sits on screen.
 */
export function Tooltip({ content, children, width = 230, className }: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const card = cardRef.current;
    if (!trigger || !card) return;
    const r = trigger.getBoundingClientRect();
    const ch = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: centre on the trigger, then clamp inside the viewport.
    let left = r.left + r.width / 2 - width / 2;
    left = Math.max(MARGIN, Math.min(left, vw - width - MARGIN));

    // Vertical: prefer above; flip below when there isn't room above; if
    // neither fits cleanly, clamp to the bottom edge.
    let top = r.top - ch - GAP;
    if (top < MARGIN) top = r.bottom + GAP;
    if (top + ch > vh - MARGIN) top = Math.max(MARGIN, vh - ch - MARGIN);

    setPos({ top, left });
  }, [width]);

  // Measure after the card mounts (useLayoutEffect runs before paint, so the
  // card is positioned before it's ever shown — no flicker). Re-measure on
  // scroll/resize while open so it stays anchored to the trigger.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  return (
    <span
      ref={triggerRef}
      className={className ?? 'relative inline-flex items-center cursor-default'}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
    >
      {children}
      {open && createPortal(
        <div
          ref={cardRef}
          role="tooltip"
          className="pointer-events-none fixed z-[10000] rounded-[var(--r2)] border border-[var(--border)] px-[10px] py-[8px] text-[11px] text-[var(--text-2)] leading-[1.5]"
          style={{
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            width,
            background: 'var(--surface)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </span>
  );
}
